// Deep Signal "Publications" tab (#134): every publication record, what it
// targeted, what it moved and at which read. Every figure, status and note
// comes from the server (app/services/publication_outcomes.py); this only lays
// it out. Clients see percentages; admins also see answer counts, the band,
// and set the intervention class, the client's index consent and query the
// cross-client precedent index.

import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  ASSET_TYPES, INTERVENTION_CLASSES, evaluatePublicationOutcomes, getPublicationOutcomes, queryPrecedents,
  rebuildPrecedents, setPrecedentConsent, tagPublication,
} from '@/api/content';
import type { OutcomeCell, OutcomeMetric, OutcomeStatus, PublicationOutcome, PublicationOutcomesView } from '@/api/content';
import type { Precedent } from '@/api/action-list';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';

const MUTED = 'var(--muted-foreground)';

export const OUTCOME_COLORS: Record<OutcomeStatus, { bg: string; fg: string }> = {
  moved: { bg: 'var(--success-bg)', fg: 'var(--success-text)' },
  reversed: { bg: 'var(--destructive-bg, var(--muted))', fg: 'var(--destructive)' },
  not_yet: { bg: 'var(--warning-bg)', fg: 'var(--warning-text)' },
  not_measured: { bg: 'var(--muted)', fg: 'var(--muted-foreground)' },
};

const btn: CSSProperties = {
  fontSize: 'var(--text-xs)', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)',
  backgroundColor: 'var(--background)', color: 'var(--foreground)', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: '4px',
};
const input: CSSProperties = {
  fontSize: 'var(--text-sm)', padding: '5px 8px', borderRadius: '6px', border: '1px solid var(--border)',
  backgroundColor: 'var(--background)', color: 'var(--foreground)',
};

function share(cell: OutcomeCell | null | undefined): string {
  if (!cell || cell.share == null) return 'Not measured';
  return `${(cell.share * 100).toFixed(1)}%`;
}

function Pct({ cell }: { cell: OutcomeCell | null | undefined }) {
  const title = cell && cell.answers != null ? `${cell.present} of ${cell.answers} answers` : undefined;
  return (
    <span title={title} style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
      {share(cell)}
      {cell?.directional && <span style={{ color: MUTED }} title="Directional: fewer than 20 answers, not counted">*</span>}
    </span>
  );
}

export function OutcomeBadge({ status, label, title }: { status: OutcomeStatus; label: string; title?: string }) {
  const c = OUTCOME_COLORS[status];
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', fontSize: 'var(--text-xs)', fontWeight: 600,
      padding: '2px 8px', borderRadius: '999px', backgroundColor: c.bg, color: c.fg, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function MetricRow({ m, admin }: { m: OutcomeMetric; admin: boolean }) {
  const what = m.name ?? (m.question_types ?? []).join(', ');
  const arrow = m.direction === 'up' ? '▲' : m.direction === 'down' ? '▼' : '';
  return (
    <tr style={{ borderTop: '1px solid var(--border)' }}>
      <td style={{ padding: '6px 8px' }}>{what}</td>
      <td style={{ padding: '6px 8px', color: MUTED }}>{m.label}</td>
      <td style={{ padding: '6px 8px', textAlign: 'right' }}><Pct cell={m.before} /></td>
      <td style={{ padding: '6px 8px', textAlign: 'right' }}><Pct cell={m.after} /></td>
      <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap',
                   color: m.direction === 'up' ? 'var(--success-text)' : m.direction === 'down' ? 'var(--destructive)' : MUTED }}
        title={m.band != null ? `Band ±${(m.band * 100).toFixed(1)} points${m.counted ? '' : ' (not counted: fewer than 20 answers)'}` : undefined}>
        {m.delta != null ? `${arrow} ${m.delta > 0 ? '+' : ''}${(m.delta * 100).toFixed(1)} pts` : ''}
        {admin && m.band != null && <span style={{ color: MUTED }}> (band {(m.band * 100).toFixed(1)})</span>}
      </td>
    </tr>
  );
}

function PublicationCard({ p, admin, customerId, onChanged }: {
  p: PublicationOutcome; admin: boolean; customerId: number; onChanged: () => void;
}) {
  const [open, setOpen] = useState(p.resolved || p.status === 'not_yet');
  const metrics = p.figures.metrics ?? [];
  const targeted = [
    ...Array.from(new Set(metrics.filter(m => m.belief_id).map(m => m.name ?? m.belief_id ?? ''))),
    ...(p.targets.question_types.length ? [`${p.targets.question_types.join(', ')} questions`] : []),
  ];
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px', backgroundColor: 'var(--background)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        <OutcomeBadge status={p.status} label={p.status_label + (p.resolved_at_label ? ` at ${p.resolved_at_label}` : '')} title={p.note} />
        {p.lasting && (
          <OutcomeBadge status={p.lasting === 'held' ? p.status : 'not_measured'}
            label={p.lasting === 'pending' ? 'Not yet followed up' : `${p.lasting_label} at the reads after`}
            title="After it resolves, the next reads are still compared with the same read before publishing" />
        )}
        {p.restarted && <OutcomeBadge status="not_measured" label="Restarted at a rebaseline" />}
        <span style={{ fontSize: 'var(--text-xs)', color: MUTED }}>
          Published {p.published_at.slice(0, 10)} · {p.destination}
        </span>
      </div>
      <p style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginTop: '6px' }}>
        {p.url ? <a href={p.url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{p.title || 'Untitled'}</a> : (p.title || 'Untitled')}
      </p>
      <p style={{ fontSize: 'var(--text-sm)', color: MUTED, marginTop: '2px' }}>
        Targeted: {targeted.length ? targeted.join('; ') : (p.targets.belief_ids.join(', ') || 'Not measured')}
      </p>
      <p style={{ fontSize: 'var(--text-sm)', marginTop: '6px', lineHeight: 1.5 }}>{p.note}</p>
      {metrics.length > 0 && (
        <button style={{ ...btn, marginTop: '8px' }} onClick={() => setOpen(o => !o)}>
          {open ? 'Hide figures' : 'Show figures'}
        </button>
      )}
      {open && metrics.length > 0 && (
        <table style={{ width: '100%', marginTop: '8px', fontSize: 'var(--text-sm)', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: MUTED, fontSize: 'var(--text-xs)', textAlign: 'left' }}>
              <th style={{ padding: '4px 8px' }}>Target</th>
              <th style={{ padding: '4px 8px' }}>Figure</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>{p.figures.ref?.read_label ?? 'Before'}</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>{p.figures.read?.read_label ?? 'After'}</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Move</th>
            </tr>
          </thead>
          <tbody>{metrics.map(m => <MetricRow key={m.key} m={m} admin={admin} />)}</tbody>
        </table>
      )}
      {p.reads.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
          {p.reads.map(r => (
            <span key={r.run_id} title={r.note} style={{
              fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--border)',
              color: r.flag === 'up' ? 'var(--success-text)' : r.flag === 'down' ? 'var(--destructive)' : MUTED,
            }}>
              {r.read_label}{r.follow_up ? ' (after)' : ''}: {r.restarted ? 'new baseline' : r.flag ? `beyond the band ${r.flag}` : r.status === 'not_measured' ? 'not measured' : 'within the band'}
            </span>
          ))}
        </div>
      )}
      {admin && <TagEditor p={p} customerId={customerId} onChanged={onChanged} />}
    </div>
  );
}

function TagEditor({ p, customerId, onChanged }: { p: PublicationOutcome; customerId: number; onChanged: () => void }) {
  const [cls, setCls] = useState(p.tags?.intervention_class ?? '');
  const [asset, setAsset] = useState(p.tags?.asset_type ?? '');
  const dirty = cls !== (p.tags?.intervention_class ?? '') || asset !== (p.tags?.asset_type ?? '');
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginTop: '10px',
                  fontSize: 'var(--text-xs)', color: MUTED }}>
      <span>Precedent index:</span>
      <select style={input} value={cls} onChange={e => setCls(e.target.value)} aria-label="Intervention class">
        <option value="">Intervention class not set</option>
        {INTERVENTION_CLASSES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <select style={input} value={asset} onChange={e => setAsset(e.target.value)} aria-label="Asset type">
        <option value="">Asset type from the draft</option>
        {ASSET_TYPES.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
      </select>
      {dirty && (
        <button style={btn} onClick={async () => {
          try {
            await tagPublication(customerId, p.publication_id, { intervention_class: cls || null, asset_type: asset || null });
            toast.success('Saved; the precedent index was rebuilt');
            onChanged();
          } catch (e: any) { toast.error(e?.message ?? 'Not saved'); }
        }}>Save</button>
      )}
    </div>
  );
}

function ConsentPanel({ view, customerId, onChanged }: { view: PublicationOutcomesView; customerId: number; onChanged: () => void }) {
  const consent = view.consent;
  const [note, setNote] = useState(consent?.note ?? '');
  if (!consent) return null;
  return (
    <div style={{ border: '1px dashed var(--border)', borderRadius: '10px', padding: '12px 14px', fontSize: 'var(--text-sm)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        <b>Cross-client precedent index</b>
        <span style={{ color: MUTED }}>Vertical: {view.vertical}</span>
        <span style={{ color: consent.consented ? 'var(--success-text)' : MUTED }}>
          {consent.consented ? 'This client consents (anonymised)' : 'Not in the index: no consent recorded'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '6px', marginTop: '8px', alignItems: 'center' }}>
        <input style={{ ...input, flex: 1 }} placeholder="Where the consent comes from (CSA clause, signed date)"
          value={note} onChange={e => setNote(e.target.value)} />
        <button style={btn} onClick={async () => {
          try {
            await setPrecedentConsent(customerId, !consent.consented, note);
            toast.success(consent.consented ? 'Consent withdrawn; index rebuilt' : 'Consent recorded; index rebuilt');
            onChanged();
          } catch (e: any) { toast.error(e?.message ?? 'Not saved'); }
        }}>{consent.consented ? 'Withdraw consent' : 'Record consent'}</button>
      </div>
      {consent.updated_by && (
        <p style={{ color: MUTED, fontSize: 'var(--text-xs)', marginTop: '4px' }}>
          Last set by {consent.updated_by} {consent.updated_at?.slice(0, 10)}
        </p>
      )}
    </div>
  );
}

function PrecedentSearch({ vertical }: { vertical?: string }) {
  const [q, setQ] = useState({ vertical: vertical ?? '', belief_category: '', intervention_class: '' });
  const [found, setFound] = useState<Precedent[] | null>(null);
  const [summary, setSummary] = useState<{ entries: number; rebuilt_at: string | null } | null>(null);
  const run = async () => {
    try {
      const out = await queryPrecedents(q);
      setFound(out.precedents);
      setSummary(out.summary);
    } catch (e: any) { toast.error(e?.message ?? 'Query failed'); }
  };
  return (
    <div style={{ border: '1px dashed var(--border)', borderRadius: '10px', padding: '12px 14px', fontSize: 'var(--text-sm)' }}>
      <b>What moved this kind of belief for this kind of brand</b>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
        <input style={input} placeholder="Vertical" value={q.vertical} onChange={e => setQ({ ...q, vertical: e.target.value })} />
        <input style={input} placeholder="Belief category (e.g. economics)" value={q.belief_category}
          onChange={e => setQ({ ...q, belief_category: e.target.value })} />
        <select style={input} value={q.intervention_class} onChange={e => setQ({ ...q, intervention_class: e.target.value })}>
          <option value="">Any intervention</option>
          {INTERVENTION_CLASSES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button style={btn} onClick={run}><Search size={12} /> Search</button>
        <button style={btn} onClick={async () => {
          try { const r = await rebuildPrecedents(); toast.success(`Index rebuilt: ${r.entries} entries`); run(); }
          catch (e: any) { toast.error(e?.message ?? 'Rebuild failed'); }
        }}><RefreshCw size={12} /> Rebuild</button>
      </div>
      {summary && (
        <p style={{ color: MUTED, fontSize: 'var(--text-xs)', marginTop: '6px' }}>
          {summary.entries} entries in the index{summary.rebuilt_at ? `, rebuilt ${summary.rebuilt_at.slice(0, 16).replace('T', ' ')}` : ''}.
          No client name, title, quote or URL is stored there.
        </p>
      )}
      {found && (found.length === 0
        ? <p style={{ color: MUTED, marginTop: '6px' }}>No precedent matches.</p>
        : <ul style={{ marginTop: '6px', paddingLeft: '18px' }}>
            {found.map(p => (
              <li key={p.id} style={{ marginTop: '4px' }}>
                {p.line}{' '}
                <span style={{ color: MUTED }}>
                  ({p.published_quarter}{p.wind_tunnel_delta != null ? `, wind tunnel ${p.wind_tunnel_delta > 0 ? '+' : ''}${p.wind_tunnel_delta.toFixed(2)}` : ''}
                  {p.days_to_move != null ? `, ${p.days_to_move} days` : ''})
                </span>
              </li>
            ))}
          </ul>)}
    </div>
  );
}

export function PublicationsSection({ customerId, isAdmin }: { customerId: number; isAdmin: boolean }) {
  const [view, setView] = useState<PublicationOutcomesView | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    getPublicationOutcomes(customerId)
      .then(v => { if (live) setView(v); })
      .catch(() => { if (live) setView(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [customerId, nonce]);

  const refresh = () => setNonce(n => n + 1);
  let body: ReactNode;
  if (loading) body = <DSLoadingRow />;
  else if (!view) body = <DSEmptyState message="Publications could not be loaded." />;
  else if (!view.available) body = <DSEmptyState message={view.note ?? 'Publication outcomes are not available yet.'} />;
  else if (!view.publications.length) body = <DSEmptyState message="No publication has been recorded for this brand yet." />;
  else body = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {view.publications.map(p => (
        <PublicationCard key={p.publication_id} p={p} admin={isAdmin} customerId={customerId} onChanged={refresh} />
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div>
        <SectionLabel>Publications</SectionLabel>
        <SectionTitle>What each publication moved</SectionTitle>
        <p style={{ fontSize: 'var(--text-sm)', color: MUTED, marginTop: '4px', maxWidth: '760px', lineHeight: 1.5 }}>
          Each publication is compared with the read before it was published, on the beliefs and question types it
          targeted. One read beyond the band is a flag; it counts as moved only when the next read holds it. Reads are
          never compared across a rebaseline.
        </p>
        {isAdmin && view?.available && (
          <button style={{ ...btn, marginTop: '8px' }} onClick={async () => {
            try {
              const r = await evaluatePublicationOutcomes(customerId);
              toast.success(r.written ? `${r.written} read check${r.written === 1 ? '' : 's'} written` : 'Nothing new to check');
              refresh();
            } catch (e: any) { toast.error(e?.message ?? 'Check failed'); }
          }}><RefreshCw size={12} /> Check against the reads now</button>
        )}
      </div>
      {body}
      {isAdmin && view?.available && <ConsentPanel view={view} customerId={customerId} onChanged={refresh} />}
      {isAdmin && view?.available && <PrecedentSearch vertical={view.vertical} />}
    </div>
  );
}
